import axios from 'axios';
import jwtDecode from 'jwt-decode';
import * as genericUtils from '@tolk-genii/generic-utils';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const isRestError = maybeError => genericUtils.isObject(maybeError) && genericUtils.isString(maybeError.error);
const isAxiosRestError = maybeAxiosError => {
  var _a;
  return genericUtils.isObject(maybeAxiosError) && axios.isAxiosError(maybeAxiosError) && isRestError((_a = maybeAxiosError.response) === null || _a === void 0 ? void 0 : _a.data);
};
const dummy = 'dummy';
const isAxiosError = maybeAxiosError => genericUtils.isObject(maybeAxiosError) && axios.isAxiosError(maybeAxiosError);
const isAxiosRestErrorNotFound = axiosError => Boolean(axiosError.response && axiosError.response.status === genericUtils.HTTP_CODE.NOT_FOUND);
const setCookie = (document, cookieDomain) => (name, value, secondsBeforeExpiration) => {
  const expirationDate = genericUtils.isNumber(secondsBeforeExpiration) ? new Date(Date.now() + secondsBeforeExpiration * genericUtils.TIME.MILLISECONDS_IN_A_SECOND) : new Date(Date.now() + genericUtils.TIME.SECONDS_IN_A_DECADE * genericUtils.TIME.MILLISECONDS_IN_A_SECOND);
  const cookie = `${name}=${value}; SameSite=Strict; Domain=${cookieDomain}; Path=/; Expires=${expirationDate.toUTCString()}`;
  // eslint-disable-next-line functional/immutable-data,unicorn/no-document-cookie
  document.cookie = cookie;
};
const removeCookie = (document, cookieDomain) => name => {
  const expirationDate = genericUtils.DATE.DEFAULT_START_DATE;
  const cookie = `${name}=; SameSite=Strict; Path=/; Domain=${cookieDomain}; Expires=${expirationDate.toUTCString()}`;
  // eslint-disable-next-line functional/immutable-data,unicorn/no-document-cookie
  document.cookie = cookie;
};
const getCookie = document => name => {
  var _a;
  const cookiesString = decodeURIComponent(document.cookie);
  const cookies = cookiesString.split('; ').reduce((existingCookies, cookieString) => {
    const [key, value] = cookieString.split('=');
    return Object.assign(Object.assign({}, existingCookies), {
      [key]: value
    });
  }, {});
  return (_a = cookies[name]) !== null && _a !== void 0 ? _a : null;
};
const refreshAccessToken = (authManagementExpressUrl, removeCookie, getCookie, setCookie) => () => __awaiter(void 0, void 0, void 0, function* () {
  removeCookie('tolk_access_token');
  const refreshToken = getCookie('tolk_refresh_token');
  try {
    const result = yield axios.post(`${authManagementExpressUrl}/v1/token`, {
      grant_type: 'refresh_token',
      refresh_token: refreshToken
    });
    if (result.data.id_token !== undefined) {
      removeCookie('tolk_id_token');
      setCookie('tolk_id_token', result.data.id_token);
    }
    removeCookie('tolk_refresh_token');
    setCookie('tolk_access_token', result.data.access_token);
    setCookie('tolk_refresh_token', result.data.refresh_token);
  } catch (_a) {}
});
const isAccessTokenExpired = () => {
  const accessToken = getCookie(document)('tolk_access_token');
  if (accessToken === null) {
    return null;
  }
  const decodedAccessToken = jwtDecode(accessToken);
  if (!genericUtils.isObject(decodedAccessToken)) {
    return null;
  }
  if (typeof decodedAccessToken.exp !== 'number') {
    return null;
  }
  if (decodedAccessToken.exp * genericUtils.TIME.MILLISECONDS_IN_A_SECOND > Date.now()) {
    return false;
  }
  return true;
};
const authenticatedRequest = (axios, authManagementExpressUrl, removeCookie, getCookie, setCookie) => (method, url, body = {}, headers = {}) => __awaiter(void 0, void 0, void 0, function* () {
  const accessToken = getCookie('tolk_access_token');
  const requestHeaders = accessToken === null ? Object.assign({}, headers) : Object.assign(Object.assign({}, headers), {
    Authorization: `Bearer ${accessToken}`
  });
  try {
    switch (method) {
      case 'get':
      case 'delete':
        return (yield axios[method](url, {
          headers: Object.assign({}, requestHeaders)
        })).data;
      case 'post':
      case 'put':
      case 'patch':
        return (yield axios[method](url, body, {
          headers: Object.assign({}, requestHeaders)
        })).data;
      default:
        throw new Error('HTTP method not allowed');
    }
  } catch (error) {
    if (isAxiosRestError(error) && error.response.status === genericUtils.HTTP_CODE.UNAUTHORIZED) {
      yield refreshAccessToken(authManagementExpressUrl, removeCookie, getCookie, setCookie)();
      const newAccessToken = getCookie('tolk_access_token');
      const newRequestHeaders = newAccessToken === null ? Object.assign({}, headers) : Object.assign(Object.assign({}, headers), {
        Authorization: `Bearer ${newAccessToken}`
      });
      switch (method) {
        case 'get':
        case 'delete':
          return (yield axios[method](url, {
            headers: Object.assign({}, newRequestHeaders)
          })).data;
        case 'post':
        case 'put':
        case 'patch':
          return (yield axios[method](url, body, {
            headers: Object.assign({}, newRequestHeaders)
          })).data;
        default:
          throw new Error('HTTP method not allowed');
      }
    }
    throw error;
  }
});
const isAuthenticated = () => {
  const accessToken = getCookie(document)('tolk_access_token');
  if (accessToken === null) {
    return false;
  }
  const decodedAccessToken = jwtDecode(accessToken);
  if (!genericUtils.isObject(decodedAccessToken)) {
    return false;
  }
  if (typeof decodedAccessToken.exp !== 'number') {
    return false;
  }
  if (decodedAccessToken.exp * genericUtils.TIME.MILLISECONDS_IN_A_SECOND < Date.now()) {
    return false;
  }
  return true;
};
const decodeAccessToken = accessToken => {
  const decodedAccessToken = jwtDecode(accessToken !== null && accessToken !== void 0 ? accessToken : '');
  if (!genericUtils.isObject(decodedAccessToken)) {
    return null;
  }
  if (typeof decodedAccessToken.sub !== 'string' || genericUtils.isEmpty(decodedAccessToken.sub)) {
    return null;
  }
  return decodedAccessToken;
};
const decodeIdToken = idToken => {
  const decodeIdToken = jwtDecode(idToken !== null && idToken !== void 0 ? idToken : '');
  if (!genericUtils.isObject(decodeIdToken)) {
    return null;
  }
  if (typeof decodeIdToken.email !== 'string' || genericUtils.isEmpty(decodeIdToken.email)) {
    return null;
  }
  if (typeof decodeIdToken.given_name !== 'string') {
    return null;
  }
  if (typeof decodeIdToken.family_name !== 'string') {
    return null;
  }
  if (typeof decodeIdToken.role !== 'string') {
    return null;
  }
  return decodeIdToken;
};
const getAuthenticatedUserId = () => {
  const accessToken = getCookie(document)('tolk_access_token');
  const accessTokenDecoded = decodeAccessToken(accessToken);
  if (accessTokenDecoded === null) {
    throw new TypeError('Invalid access token');
  }
  if (accessTokenDecoded.nonce === 'app') {
    throw new TypeError('Invalid access token');
  }
  return accessTokenDecoded.sub;
};
const getAuthenticatedUserEmail = () => {
  const idToken = getCookie(document)('tolk_id_token');
  const idTokenDecoded = decodeIdToken(idToken);
  if (idTokenDecoded === null) {
    throw new TypeError('Invalid id token');
  }
  return idTokenDecoded.email;
};
const getAuthenticatedUserGivenName = () => {
  const idToken = getCookie(document)('tolk_id_token');
  const idTokenDecoded = decodeIdToken(idToken);
  if (idTokenDecoded === null) {
    throw new TypeError('Invalid id token');
  }
  return idTokenDecoded.given_name;
};
const getAuthenticatedUserFamilyName = () => {
  const idToken = getCookie(document)('tolk_id_token');
  const idTokenDecoded = decodeIdToken(idToken);
  if (idTokenDecoded === null) {
    throw new TypeError('Invalid id token');
  }
  return idTokenDecoded.family_name;
};
const getAuthenticatedUserRole = () => {
  const idToken = getCookie(document)('tolk_id_token');
  const idTokenDecoded = decodeIdToken(idToken);
  if (idTokenDecoded === null) {
    throw new TypeError('Invalid id token');
  }
  return idTokenDecoded.role;
};
const isTolkAdmin = () => {
  const idToken = getCookie(document)('tolk_id_token');
  const idTokenDecoded = decodeIdToken(idToken);
  if (idTokenDecoded === null) {
    throw new TypeError('Invalid id token');
  }
  return ['tolk-admin', 'tolk-super-admin'].includes(idTokenDecoded.role);
};
const getGivenNameAndPartialFamilyName = () => {
  const givenName = getAuthenticatedUserGivenName().toLowerCase();
  const familyName = getAuthenticatedUserFamilyName().toLowerCase();
  return `${givenName.replace(/^(?:[0-9A-Z_a-z])/, letter => letter.toUpperCase())} ${familyName.charAt(genericUtils.ARRAY.FIRST_INDEX).toUpperCase()}.`;
};
const logout = (authenticatedRequest, cookieDomain) => () => {
  const refreshToken = getCookie(document)('tolk_refresh_token');
  if (genericUtils.isString(refreshToken)) {
    void authenticatedRequest('post', '/v1/revoke', {
      refresh_token: refreshToken
    });
  }
  removeCookie(document, cookieDomain)('tolk_access_token');
  removeCookie(document, cookieDomain)('tolk_id_token');
  removeCookie(document, cookieDomain)('tolk_refresh_token');
  window.localStorage.removeItem('tolk_authorization_infos');
  window.location.assign('/');
};

export { authenticatedRequest, dummy, getAuthenticatedUserEmail, getAuthenticatedUserFamilyName, getAuthenticatedUserGivenName, getAuthenticatedUserId, getAuthenticatedUserRole, getCookie, getGivenNameAndPartialFamilyName, isAccessTokenExpired, isAuthenticated, isAxiosError, isAxiosRestError, isAxiosRestErrorNotFound, isRestError, isTolkAdmin, logout, refreshAccessToken, removeCookie, setCookie };
